import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import ClockIcon from "@atlaskit/icon/core/clock";
import {KanbanCardRowActions} from "~/components/ottopilotDataViewer/rowEngagement/KanbanCardRowActions";
import {resolveKanbanPhoneDial} from "~/components/ottopilotDataViewer/rowEngagement/resolveKanbanPhoneDial";
import type {TableRowActionsConfig} from "~/components/table/tableRowActions.types";
import {token} from "@atlaskit/tokens";
import {useMemo, useState} from "react";
import {twJoin} from "tailwind-merge";
import {EventAdditionalButtonFormModal} from "~/components/eventAdditionalButtonFormModal";
import {KanbanCardDispositionControl} from "~/components/ottopilotDataViewer/kanbanCardDispositionControl";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {
    getEventContainerDispositionOptions,
    getOptionalEventContainerDispositionId,
} from "~/utilities/ottopilotSemanticColumns";
import type {BtlActivity} from "~/domainModels/btlActivities";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {
    OttopilotAdditionalButtonKind,
    type OttopilotAdditionalButtonsConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {eventContainerKanbanCardIconsMap as journeyKanbanCardIconsMap} from "~/domainModels/eventContainerPanelIcons";

import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {resolveDuluxEventKanbanBoardCardView} from "~/domainModels/workspaceSpecific/dulux";
import {
    AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    AmaronEventTypes,
    isAmaronKanbanListEventTemplate,
    isAmaronWorkspace,
    resolveAmaronEventKanbanBoardCardView,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    AnankeDatabaseStorageCacheIcon,
    AnankeDevicesIcon,
    AnankeInformationCircleIcon,
    AnankeServiceIcon,
} from "~/domainModels/workspaceSpecific/icons";
import {
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EmotoradEventTypes,
    isEmotoradWorkspace,
} from "~/domainModels/workspaceSpecific/emotorad";
import {
    isKanbanCardSystemFieldId,
    type KanbanCardSystemFieldId,
} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {useOnlyEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

type KanbanCardDetail = {
    iconId: Uuid;
    label: string;
    value: string;
};
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import {formatKanbanCardFieldValue} from "~/utilities/kanbanCardDisplay";
import {
    dispositionToLevelFormat,
    formatDateAndTime,
    formatDateTime,
    getUserDisplayName,
} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";

function getEventKanbanSystemFieldStringValue({
    field,
    event,
    users,
    userTemplates,
    platformUserDetailsByUserId,
}: {
    field: KanbanCardSystemFieldId;
    event: EventWithEventContainer;
    users: User[];
    userTemplates: UserTemplate[];
    platformUserDetailsByUserId: ReadonlyMap<Uuid, ReturnType<typeof useWorkspace>["platformUserDetails"][number]>;
}): string | null {
    switch (field) {
        case "createdBy": {
            return getUserDisplayName(event.createdBy, users, userTemplates, platformUserDetailsByUserId);
        }
        case "updatedBy": {
            return getUserDisplayName(event.updatedBy, users, userTemplates, platformUserDetailsByUserId);
        }
        case "createdAt": {
            return formatDateTime(event.createdAt);
        }
        case "updatedAt": {
            return formatDateTime(event.updatedAt);
        }
        default: {
            const _exhaustive: never = field;
            throw new Error(`Unhandled kanban system field: ${String(_exhaustive)}`);
        }
    }
}

function getPhoneLikeColumnDisplayString(getAdditionalColumns: CapabilityUdoColumnResolver, {
    columnId,
    event,
    eventTemplate,
    eventTemplates,
    users,
    userTemplates,
    organizationalUnits,
    organizationalUnitTemplates,
    journeysModuleConfiguration,
    products,
}: {
    columnId: Uuid | null;
    event: EventWithEventContainer;
    eventTemplate: OttopilotEventTemplate;
    eventTemplates: OttopilotEventTemplate[];
    users: User[];
    userTemplates: UserTemplate[];
    organizationalUnits: OrganizationalUnit[];
    organizationalUnitTemplates: OrganizationalUnitTemplate[];
    journeysModuleConfiguration: JourneysModuleConfiguration;
    btlActivities: Array<BtlActivity>;
    products: Array<ProductCatalogue>;
}): string | null {
    if (columnId === null) {
        return null;
    }
    const eventColumn = getAdditionalColumns(eventTemplate).find((col) => col.id === columnId);
    if (eventColumn !== undefined) {
        const value = event.additionalColumnValues[columnId];
        if (value === undefined || value === null) {
            return null;
        }
        return getUserDefinedObjectAdditionalColumnDetails(eventColumn).stringValueGetter({
            value: value as never,
            users: users,
            userTemplates: userTemplates,
            products: products,
            organizationalUnits: organizationalUnits,
            organizationalUnitTemplates: organizationalUnitTemplates,
        });
    }
    const journeyColumn = getAdditionalColumns(journeysModuleConfiguration).find((col) => col.id === columnId);
    if (journeyColumn === undefined || event.eventContainer === undefined) {
        return null;
    }
    const journeyValue = event.eventContainer.additionalColumnValues[columnId];
    if (journeyValue === undefined) {
        return null;
    }
    const eventTemplateForJourney = eventTemplates.find((et) => et.id === event.templateId) ?? eventTemplates[0];
    if (eventTemplateForJourney === undefined) {
        return null;
    }
    return getUserDefinedObjectAdditionalColumnDetails(journeyColumn).stringValueGetter({
        value: journeyValue as never,
        users: users,
        userTemplates: userTemplates,
        organizationalUnits: organizationalUnits,
        organizationalUnitTemplates: organizationalUnitTemplates,
        products: products,
    });
}

/** Emotorad Calls kanban: journey "Assigned dealer" may store an OU id while column type is not `ou`. */
function resolveEmotoradCallKanbanAssignedDealerDisplayValue({
    rawValue,
    organizationalUnits,
    organizationalUnitTemplates,
    users,
    userTemplates,
    platformUserDetailsByUserId,
}: {
    rawValue: unknown;
    organizationalUnits: OrganizationalUnit[];
    organizationalUnitTemplates: OrganizationalUnitTemplate[];
    users: User[];
    userTemplates: UserTemplate[];
    platformUserDetailsByUserId: ReadonlyMap<Uuid, ReturnType<typeof useWorkspace>["platformUserDetails"][number]>;
}): string | null {
    if (typeof rawValue !== "string") {
        return null;
    }
    const trimmed = rawValue.trim();
    if (trimmed === "") {
        return null;
    }
    const ou = organizationalUnits.find((u) => u.id === trimmed);
    if (ou !== undefined) {
        const ouTemplate = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
        if (ouTemplate !== undefined) {
            return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
        }
    }
    const userDisplayName = getUserDisplayName(trimmed as Uuid, users, userTemplates, platformUserDetailsByUserId);
    if (userDisplayName !== "?" && userDisplayName !== "-") {
        return userDisplayName;
    }
    return null;
}

function getKanbanCardDataFromViewDetailsForEvents(getAdditionalColumns: CapabilityUdoColumnResolver, {
    event,
    journeysModuleConfiguration,
    users,
    userTemplates,
    platformUserDetailsByUserId,
    organizationalUnits,
    organizationalUnitTemplates,
    eventTemplates,
    products,
    emotoradCallKanbanResolveAssignedDealer,
    fillEmptyDetailSlots = false,
}: {
    event: EventWithEventContainer;
    journeysModuleConfiguration: JourneysModuleConfiguration;
    users: User[];
    userTemplates: UserTemplate[];
    platformUserDetailsByUserId: ReadonlyMap<Uuid, ReturnType<typeof useWorkspace>["platformUserDetails"][number]>;
    organizationalUnits: OrganizationalUnit[];
    organizationalUnitTemplates: OrganizationalUnitTemplate[];
    eventTemplates: OttopilotEventTemplate[];
    btlActivities: Array<BtlActivity>;
    products: Array<ProductCatalogue>;
    emotoradCallKanbanResolveAssignedDealer: boolean;
    fillEmptyDetailSlots?: boolean;
}): {heading: string | null; details: KanbanCardDetail[][]} {
    const eventTemplate = eventTemplates.find((item) => item.id === event.templateId);
    if (eventTemplate === undefined) {
        return {heading: null, details: []};
    }
    const {details: data, heading: headingArray} = resolveAmaronEventKanbanBoardCardView(
        eventTemplate.id,
        resolveDuluxEventKanbanBoardCardView(eventTemplate.id, eventTemplate.configuration.eventPage.kanbanBoardCardView),
    );

    const eventColumnTypeMap = new Map(getAdditionalColumns(eventTemplate).map((col) => [col.id, col.type]));
    const eventColumnDisplayNameMap = new Map(getAdditionalColumns(eventTemplate).map((col) => [col.id, col.displayName]));
    const journeyColumnIdByDisplayNameMap = new Map(getAdditionalColumns(journeysModuleConfiguration).map((col) => [col.displayName, col.id]));

    const getStringValue = (dataSourceId: Uuid | KanbanCardSystemFieldId): string | null => {
        if (isKanbanCardSystemFieldId(dataSourceId) === true) {
            return getEventKanbanSystemFieldStringValue({
                field: dataSourceId,
                event: event,
                users: users,
                userTemplates: userTemplates,
                platformUserDetailsByUserId,
            });
        }
        const columnUuid = dataSourceId as Uuid;
        // Check if this column exists in event
        const eventType = eventColumnTypeMap.get(columnUuid);
        if (eventType !== undefined) {
            const eventDisplayName = eventColumnDisplayNameMap.get(columnUuid);
            // Check if there's a journey column with the same display name (common field)
            if (eventDisplayName !== undefined) {
                const journeyColumnId = journeyColumnIdByDisplayNameMap.get(eventDisplayName);
                if (journeyColumnId !== undefined) {
                    // Use journey value for common fields
                    const journeyColumn = getAdditionalColumns(journeysModuleConfiguration).find((col) => col.id === journeyColumnId);
                    if (journeyColumn !== undefined) {
                        const eventTemplateForJourney = eventTemplates.find((et) => et.id === event.templateId) ?? eventTemplates[0];
                        if (eventTemplateForJourney === undefined) {
                            return null;
                        }
                        const journeyValue = event.eventContainer?.additionalColumnValues[journeyColumnId];
                        if (journeyValue === undefined) {
                            return null;
                        }
                        if (
                            emotoradCallKanbanResolveAssignedDealer === true &&
                            String(journeyColumnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID)
                        ) {
                            const resolved = resolveEmotoradCallKanbanAssignedDealerDisplayValue({
                                rawValue: journeyValue,
                                organizationalUnits,
                                organizationalUnitTemplates,
                                users,
                                userTemplates,
                                platformUserDetailsByUserId,
                            });
                            if (resolved !== null) {
                                return resolved;
                            }
                        }
                        if (journeyColumn.type === UdoColumnType.user) {
                            return getUserDisplayName(journeyValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                        }
                        return getUserDefinedObjectAdditionalColumnDetails(journeyColumn).stringValueGetter({
                            value: journeyValue as never,
                            users: users,
                            userTemplates: userTemplates,
                            organizationalUnits: organizationalUnits,
                            organizationalUnitTemplates: organizationalUnitTemplates,
                            products: products,
                        });
                    }
                }
            }
            // Use event value if no common journey field found
            if (eventType === UdoColumnType.user) {
                return getUserDisplayName(event.additionalColumnValues[columnUuid] as Uuid, users, userTemplates, platformUserDetailsByUserId);
            }
            return getUserDefinedObjectAdditionalColumnDetails(getAdditionalColumns(eventTemplate).find((column) => column.id === columnUuid)!).stringValueGetter({
                value: event?.additionalColumnValues[columnUuid] as never,
                users,
                userTemplates,
                products,
                organizationalUnits: organizationalUnits,
                organizationalUnitTemplates: organizationalUnitTemplates,
            });
        }
        // Check if this column exists in journey
        const journeyColumn = getAdditionalColumns(journeysModuleConfiguration).find((col) => col.id === columnUuid);
        if (journeyColumn !== undefined) {
            const eventTemplateForJourney = eventTemplates.find((et) => et.id === event.templateId) ?? eventTemplates[0];
            if (eventTemplateForJourney === undefined) {
                return null;
            }
            const journeyValue = event.eventContainer?.additionalColumnValues[columnUuid];
            if (journeyValue === undefined) {
                return null;
            }
            if (emotoradCallKanbanResolveAssignedDealer === true && String(columnUuid) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID)) {
                const resolved = resolveEmotoradCallKanbanAssignedDealerDisplayValue({
                    rawValue: journeyValue,
                    organizationalUnits,
                    organizationalUnitTemplates,
                    users,
                    userTemplates,
                    platformUserDetailsByUserId,
                });
                if (resolved !== null) {
                    return resolved;
                }
            }
            if (journeyColumn.type === UdoColumnType.user) {
                return getUserDisplayName(journeyValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
            }
            return getUserDefinedObjectAdditionalColumnDetails(journeyColumn).stringValueGetter({
                value: journeyValue as never,
                users: users,
                userTemplates: userTemplates,
                organizationalUnits: organizationalUnits,
                organizationalUnitTemplates: organizationalUnitTemplates,
                products: products,
            });
        }
        return null;
    };
    const details = data.map((column) =>
        column.map((columnDetail) => {
            let value: string | null = null;

            if (columnDetail.type === "SingleColumn") {
                value = getStringValue(columnDetail.dataSource);
            } else if (columnDetail.type === "MultipleColumn") {
                const values = columnDetail.dataSource.map(getStringValue).filter((v): v is string => v !== null);
                value = values.length > 0 ? values.join(columnDetail.seperator) : null;
            }

            if (fillEmptyDetailSlots === true) {
                return {
                    iconId: columnDetail.iconId,
                    label: columnDetail.label,
                    value: formatKanbanCardFieldValue(value),
                };
            }

            return value !== null ? {iconId: columnDetail.iconId, label: columnDetail.label, value} : null;
        }).filter((item): item is KanbanCardDetail => item !== null),
    );
    let headingValue: string | null = null;
    for (const item of headingArray) {
        const value = getStringValue(item);
        if (value !== null) {
            headingValue = value;
            break;
        }
    }
    return {
        heading: fillEmptyDetailSlots === true ? formatKanbanCardFieldValue(headingValue) : (headingValue ?? null),
        details,
    };
}

/** Emotorad Calls kanban only: fixed rows (labels matched case-insensitively to template copy). */
const EMOTORAD_CALLS_KANBAN_CARD_DETAIL_ROWS: ReadonlyArray<ReadonlyArray<string>> = [
    ["Pincode", "Model Variant"],
    ["Assigned Dealer", "Logged On"],
    ["Scheduled By", "Logged By"],
];

function normalizeKanbanDetailLabel(label: string): string {
    return label.trim().toLowerCase();
}

function buildEmotoradCallsKanbanDetailRows(allDetails: KanbanCardDetail[]): KanbanCardDetail[][] {
    const byNormalizedLabel = new Map<string, KanbanCardDetail>();
    for (const detail of allDetails) {
        byNormalizedLabel.set(normalizeKanbanDetailLabel(detail.label), detail);
    }
    const used = new Set<string>();
    const rows: KanbanCardDetail[][] = [];
    for (const labelGroup of EMOTORAD_CALLS_KANBAN_CARD_DETAIL_ROWS) {
        const row: KanbanCardDetail[] = [];
        for (const label of labelGroup) {
            const key = normalizeKanbanDetailLabel(label);
            const detail = byNormalizedLabel.get(key);
            if (detail !== undefined) {
                row.push(detail);
                used.add(key);
            }
        }
        if (row.length > 0) {
            rows.push(row);
        }
    }
    for (const detail of allDetails) {
        const key = normalizeKanbanDetailLabel(detail.label);
        if (used.has(key) === false) {
            rows.push([detail]);
            used.add(key);
        }
    }
    return rows;
}

function isKanbanRemarksDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized === "remarks" || normalized === "remark" || normalized.includes("remark");
}

function isKanbanServiceRequestedDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("service") && normalized.includes("request");
}

function isKanbanUtmPlatformDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("utm") === true && normalized.includes("platform") === true;
}

function isKanbanUtmSourceDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("utm") && normalized.includes("source");
}

function splitAmaronEnquiryKanbanCardDetails(details: KanbanCardDetail[][]): KanbanCardDetail[][] {
    const remainingKanbanCardData: KanbanCardDetail[][] = [];
    for (const row of details) {
        const remainingRow: KanbanCardDetail[] = [];
        for (const element of row) {
            if (isKanbanServiceRequestedDetailLabel(element.label) === true) {
                continue;
            }
            if (isKanbanUtmSourceDetailLabel(element.label) === true) {
                continue;
            }
            if (isKanbanUtmPlatformDetailLabel(element.label) === true) {
                continue;
            }
            if (isKanbanRemarksDetailLabel(element.label) === true) {
                continue;
            }
            remainingRow.push(element);
        }
        if (remainingRow.length > 0) {
            remainingKanbanCardData.push(remainingRow);
        }
    }
    return remainingKanbanCardData;
}

function resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns: CapabilityUdoColumnResolver, {
    journey,
    columnId,
    journeyModuleConfiguration,
    users,
    userTemplates,
    organizationalUnits,
    organizationalUnitTemplates,
    products,
}: {
    journey: OttopilotEventContainer;
    columnId: Uuid;
    journeyModuleConfiguration: JourneysModuleConfiguration;
    users: User[];
    userTemplates: UserTemplate[];
    btlActivities: Array<BtlActivity>;
    organizationalUnits: OrganizationalUnit[];
    organizationalUnitTemplates: OrganizationalUnitTemplate[];
    eventTemplates: Array<OttopilotEventTemplate>;
    products: Array<ProductCatalogue>;
}): string {
    const column = getAdditionalColumns(journeyModuleConfiguration).find((col) => col.id === columnId);
    if (column === undefined) {
        return formatKanbanCardFieldValue(null);
    }
    const rawValue = journey.additionalColumnValues[columnId];
    if (rawValue === null || rawValue === undefined) {
        return formatKanbanCardFieldValue(null);
    }
    if (typeof rawValue === "string" && rawValue.trim() === "") {
        return formatKanbanCardFieldValue(null);
    }
    const displayValue = getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
        value: journey.additionalColumnValues[columnId] as never,
        users,
        userTemplates,
        organizationalUnits,
        organizationalUnitTemplates,
        products,
    });
    if (displayValue === "?" || displayValue.trim() === "") {
        return formatKanbanCardFieldValue(null);
    }
    return displayValue;
}

function AmaronKanbanDetailFieldRow({detail}: {detail: KanbanCardDetail}) {
    const Icon = journeyKanbanCardIconsMap[detail.iconId];
    const value = formatKanbanCardFieldValue(detail.value);
    return (
        <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-ds-space-100 items-start min-w-0">
            <div className="w-6 h-6 flex shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">{Icon !== undefined ? Icon({label: ""}) : null}</div>
            <div className="min-w-0">
                <div
                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1 text-ellipsis"
                    style={{
                        fontFamily: "Inter",
                        color: token("color.text.subtlest"),
                    }}
                >
                    {detail.label}
                </div>
                <div
                    className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-2 text-ellipsis"
                    style={{
                        fontFamily: "Inter",
                        color: token("color.text"),
                    }}
                >
                    {value}
                </div>
            </div>
        </div>
    );
}

function AmaronKanbanSecondaryDetailsSection({
    serviceRequestedDetail,
    remarksDetail,
    utmPlatformDetail,
    utmSourceDetail,
}: {
    serviceRequestedDetail: KanbanCardDetail;
    remarksDetail: KanbanCardDetail;
    utmPlatformDetail: KanbanCardDetail;
    utmSourceDetail: KanbanCardDetail;
}) {
    return (
        <>
            <VerticalSpacer className="h-ds-space-150" />
            <div className="w-full h-px bg-ds-border" />
            <VerticalSpacer className="h-ds-space-150" />
            <div className="grid grid-cols-2 gap-x-ds-space-150 gap-y-ds-space-100 items-start">
                <div className="min-w-0">
                    <AmaronKanbanDetailFieldRow detail={serviceRequestedDetail} />
                </div>
                <div className="min-w-0">
                    <AmaronKanbanDetailFieldRow detail={remarksDetail} />
                </div>
                <div className="min-w-0">
                    <AmaronKanbanDetailFieldRow detail={utmPlatformDetail} />
                </div>
                <div className="min-w-0">
                    <AmaronKanbanDetailFieldRow detail={utmSourceDetail} />
                </div>
            </div>
        </>
    );
}

function EventKanbanCardDetailFieldRow(props: {element: KanbanCardDetail}) {
    const {element} = props;
    return (
        <div
            className="grid gap-ds-space-100 grid-flow-col justify-start items-start"
            style={{
                fontFamily: "Inter",
                color: token("color.text.subtlest"),
            }}
        >
            {journeyKanbanCardIconsMap[element.iconId]!({
                label: "",
            })}
            <div className="min-w-0">
                <div
                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1 text-ellipsis"
                    style={{
                        fontFamily: "Inter",
                        color: token("color.text.subtlest"),
                    }}
                >
                    {element.label}
                </div>
                <div
                    className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-1 text-ellipsis"
                    style={{
                        fontFamily: "Inter",
                        color: token("color.text"),
                    }}
                >
                    {element.value}
                </div>
            </div>
        </div>
    );
}

export function EventKanbanBoardCard(props: {
    event: EventWithEventContainer;
    rowActionsConfig?: TableRowActionsConfig<EventWithEventContainer>;
    onRequestAdditionalButtonModal?: (p: {config: OttopilotAdditionalButtonsConfig; event: EventWithEventContainer}) => void;
    dataViewerQueryKeyPrefix?: Array<unknown>;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {event} = props;
    const {workspace, workspaceCapabilities, platformUserDetails, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const eventTemplates = useEventTemplates();
    const eventTemplate = eventTemplates.find((item) => item.id === event.templateId);
    if (eventTemplate === undefined) {
        console.log("event = ", event);
        console.log("eventTemplates = ", eventTemplates);
        throw new Error("Event template not found");
    }
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const journeysModuleConfiguration = useOnlyEventContainerTemplate();
    const journeyDispositionId = getOptionalEventContainerDispositionId(event.eventContainer, journeysModuleConfiguration);
    const journeyDispositionOptions = getEventContainerDispositionOptions(getAdditionalColumns, journeysModuleConfiguration, userDefinedObjectOptionsByTemplateId);
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const products = useProductCatalogue() ?? [];
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );
    const emotoradCallKanbanResolveAssignedDealer =
        isEmotoradWorkspace(workspace) === true &&
        (event.templateId === (EmotoradEventTypes.call as Uuid) ||
            event.templateId === (EmotoradEventTypes.testride as Uuid) ||
            event.templateId === (EmotoradEventTypes.walkin as Uuid));
    const fillAmaronKanbanDetailSlots =
        isAmaronWorkspace(workspaceCapabilities) === true && isAmaronKanbanListEventTemplate(event.templateId) === true;
    const isAmaronEnquiryKanban =
        isAmaronWorkspace(workspaceCapabilities) === true && event.templateId === (AmaronEventTypes.Enquiry as Uuid);

    const contactNumberColumn = eventTemplate.configuration.eventPage.callButtonColumnId;
    const contactNumber = useMemo(
        () =>
            getPhoneLikeColumnDisplayString(getAdditionalColumns, {
                columnId: contactNumberColumn,
                event: event,
                eventTemplate: eventTemplate,
                eventTemplates: eventTemplates,
                users: users,
                userTemplates: userTemplates,
                organizationalUnits: organizationalUnits,
                organizationalUnitTemplates: organizationalUnitTemplates,
                journeysModuleConfiguration: journeysModuleConfiguration,
                btlActivities: btlActivities,
                products: products,
            }),
        [getAdditionalColumns,
            contactNumberColumn,
            event,
            eventTemplate,
            eventTemplates,
            users,
            userTemplates,
            organizationalUnits,
            organizationalUnitTemplates,
            journeysModuleConfiguration,
            btlActivities,
            products,
        ],
    );
    const kanbanPhoneDial = useMemo(() => {
        const raw = contactNumberColumn !== null && contactNumberColumn !== undefined ? event.additionalColumnValues[contactNumberColumn] : contactNumber;
        return resolveKanbanPhoneDial(raw, contactNumber ?? undefined);
    }, [contactNumber, contactNumberColumn, event.additionalColumnValues]);

    const {date: createdDate, time: createdTime} = formatDateAndTime(event.createdAt);
    const {heading: kanbanCardHeading, details: kanbanCardData} = useMemo(
        () =>
            getKanbanCardDataFromViewDetailsForEvents(getAdditionalColumns, {
                journeysModuleConfiguration: journeysModuleConfiguration,
                event,
                users,
                userTemplates,
                platformUserDetailsByUserId,
                organizationalUnits: organizationalUnits,
                organizationalUnitTemplates: organizationalUnitTemplates,
                eventTemplates: eventTemplates,
                btlActivities: btlActivities,
                products: products,
                emotoradCallKanbanResolveAssignedDealer,
                fillEmptyDetailSlots: fillAmaronKanbanDetailSlots,
            }),
        [getAdditionalColumns,
            event.id,
            journeysModuleConfiguration,
            event,
            users,
            userTemplates,
            platformUserDetailsByUserId,
            organizationalUnits,
            organizationalUnitTemplates,
            eventTemplates,
            btlActivities,
            products,
            emotoradCallKanbanResolveAssignedDealer,
            fillAmaronKanbanDetailSlots,
        ],
    );
    const amaronEnquiryKanbanSecondaryDetails = useMemo(() => {
        if (isAmaronEnquiryKanban !== true || event.eventContainer === undefined) {
            return null;
        }
        const journeyKanbanColumnDeps = {
            journey: event.eventContainer,
            journeyModuleConfiguration: journeysModuleConfiguration,
            users,
            userTemplates,
            btlActivities,
            organizationalUnits,
            organizationalUnitTemplates,
            eventTemplates,
            products,
        };
        const serviceRequestedColumn = getAdditionalColumns(journeysModuleConfiguration).find(
            (column) => column.id === AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
        );
        const utmPlatformColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.id === AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID);
        const utmSourceColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.id === AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID);
        const remarksColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.id === AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID);
        const remarksLabel = remarksColumn?.displayName.replace(/\s*\(Recent\)\s*/i, "").trim() || "Remarks";
        const serviceRequestedDetail: KanbanCardDetail = {
            iconId: AnankeServiceIcon,
            label: serviceRequestedColumn?.displayName ?? "Service requested",
            value: resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns, {
                ...journeyKanbanColumnDeps,
                columnId: AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
            }),
        };
        const remarksDetail: KanbanCardDetail = {
            iconId: AnankeInformationCircleIcon,
            label: remarksLabel,
            value: resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns, {
                ...journeyKanbanColumnDeps,
                columnId: AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
            }),
        };
        const utmPlatformDetail: KanbanCardDetail = {
            iconId: AnankeDevicesIcon,
            label: utmPlatformColumn?.displayName ?? "UTM Platform",
            value: resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns, {
                ...journeyKanbanColumnDeps,
                columnId: AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
            }),
        };
        const utmSourceDetail: KanbanCardDetail = {
            iconId: AnankeDatabaseStorageCacheIcon,
            label: utmSourceColumn?.displayName ?? "UTM Source",
            value: resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns, {
                ...journeyKanbanColumnDeps,
                columnId: AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
            }),
        };
        return {serviceRequestedDetail, remarksDetail, utmPlatformDetail, utmSourceDetail};
    }, [getAdditionalColumns,
        isAmaronEnquiryKanban,
        event.id,
        event.eventContainer,
        journeysModuleConfiguration,
        users,
        userTemplates,
        btlActivities,
        organizationalUnits,
        organizationalUnitTemplates,
        eventTemplates,
        products,
    ]);
    const cardDetailsToRender = useMemo(() => {
        if (isAmaronEnquiryKanban !== true) {
            return kanbanCardData;
        }
        return splitAmaronEnquiryKanbanCardDetails(kanbanCardData);
    }, [isAmaronEnquiryKanban, kanbanCardData]);
    const [isHovered, setIsHovered] = useState(false);
    const {workspaceUser} = useWorkspaceUser();
    const [localAdditionalButtonModal, setLocalAdditionalButtonModal] = useState<{
        config: OttopilotAdditionalButtonsConfig;
        event: EventWithEventContainer;
    } | null>(null);

    const queryKeyPrefixForEmbeddedModal = props.dataViewerQueryKeyPrefix ?? [];

    return (
        <div
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className={twJoin(`w-full`, "shadow-sm", "relative group", "max-sm:hidden")}
                style={{
                    borderRadius: token("radius.small"),
                    background: isHovered ? token("color.background.disabled") : token("color.icon.inverse"),
                    boxShadow: "0 0 1px 0 rgba(30, 31, 33, 0.12), 0 0 8px 0 rgba(30, 31, 33, 0.16)",
                }}
            >
                <div className="w-full pt-ds-space-200 px-ds-space-200 pb-ds-space-150">
                    <div className="grid grid-flow-col gap-ds-space-100 justify-between">
                        <div className="line-clamp-1 overflow-hidden">
                            <div
                                className="text-[1rem] font-ds-font-weight-bold leading-ds-space-300 line-clamp-1 text-ellipsis"
                                style={{fontFamily: "Inter", color: token("color.text")}}
                            >
                                {kanbanCardHeading ?? (fillAmaronKanbanDetailSlots === true ? formatKanbanCardFieldValue(null) : null)}
                            </div>
                        </div>
                        <KanbanCardRowActions
                            row={event}
                            config={props.rowActionsConfig}
                            phoneDial={kanbanPhoneDial}
                        />
                    </div>
                    <VerticalSpacer className="h-ds-space-100" />
                    <div className="flex flex-wrap justify-between gap-ds-space-100">
                        <div className="line-clamp-1 text-right grid grid-flow-col justify-start gap-x-ds-space-075 items-center">
                            <ClockIcon label="" />
                            <span
                                className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200"
                                style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                            >
                                {createdDate}, {createdTime}
                            </span>
                            <span className="inline-flex items-center justify-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="6"
                                    height="6"
                                    viewBox="0 0 6 6"
                                    fill="none"
                                >
                                    <circle
                                        cx="3"
                                        cy="3"
                                        r="3"
                                        fill={token("color.background.accent.gray.subtler", "#DDDEE1")}
                                    />
                                </svg>
                            </span>
                            {journeyDispositionId !== null
                                ? (() => {
                                      const disposition = journeyDispositionOptions.find((d) => d.id === journeyDispositionId);
                                      const label = disposition !== undefined ? dispositionToLevelFormat(disposition.displayName) : "—";
                                      return (
                                          <KanbanCardDispositionControl
                                              label=""
                                              tooltip={`Dispositions:\n${label}`}
                                              compact={true}
                                          />
                                      );
                                  })()
                                : null}
                        </div>
                    </div>
                    {amaronEnquiryKanbanSecondaryDetails !== null ? (
                        <AmaronKanbanSecondaryDetailsSection
                            serviceRequestedDetail={amaronEnquiryKanbanSecondaryDetails.serviceRequestedDetail}
                            remarksDetail={amaronEnquiryKanbanSecondaryDetails.remarksDetail}
                            utmPlatformDetail={amaronEnquiryKanbanSecondaryDetails.utmPlatformDetail}
                            utmSourceDetail={amaronEnquiryKanbanSecondaryDetails.utmSourceDetail}
                        />
                    ) : null}
                    {cardDetailsToRender.length > 0 ? (
                        <>
                            <VerticalSpacer className="h-ds-space-150" />
                            <div className="w-full h-px bg-ds-border" />
                            <VerticalSpacer className="h-ds-space-150" />
                            <div className="grid gap-ds-space-150">
                                {emotoradCallKanbanResolveAssignedDealer === true ? (
                                    <>
                                        {buildEmotoradCallsKanbanDetailRows(kanbanCardData.flat()).map((row, rowIdx) => (
                                            <div
                                                key={rowIdx}
                                                className={twJoin("grid gap-ds-space-150 items-start", row.length === 2 ? "grid-cols-2" : "grid-cols-1")}
                                            >
                                                {row.map((element, colIdx) => (
                                                    <div
                                                        key={`${rowIdx}-${colIdx}`}
                                                        className="min-w-0"
                                                    >
                                                        <EventKanbanCardDetailFieldRow element={element} />
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <ItemBuilder
                                        items={cardDetailsToRender}
                                        itemBuilder={(elements, rowIdx) => (
                                            <div
                                                key={rowIdx}
                                                className={twJoin("grid gap-ds-space-150 items-start", elements.length === 2 ? "grid-cols-2" : "grid-cols-1")}
                                            >
                                                <ItemBuilder
                                                    items={elements}
                                                    itemBuilder={(element, elementId) => (
                                                        <div
                                                            key={elementId}
                                                            className="min-w-0"
                                                        >
                                                            <EventKanbanCardDetailFieldRow element={element} />
                                                        </div>
                                                    )}
                                                />
                                            </div>
                                        )}
                                    />
                                )}
                            </div>
                        </>
                    ) : null}

                    <VerticalSpacer className="h-ds-space-100" />
                </div>
            </div>
            <div
                className={twJoin(`w-full`, "shadow-sm", "relative group", "sm:hidden")}
                style={{
                    borderRadius: token("radius.small"),
                    background: token("color.icon.inverse"),
                    boxShadow: "0 0 1px 0 rgba(30, 31, 33, 0.12), 0 0 8px 0 rgba(30, 31, 33, 0.16)",
                }}
            >
                <div className="w-full pt-ds-space-200 px-ds-space-200 pb-ds-space-150 grid grid-cols-[auto_auto] gap-ds-space-100 justify-between items-center">
                    <div className="grid gap-ds-space-100 justify-start">
                        <div className="line-clamp-1 overflow-hidden">
                            <div
                                className="text-[1rem] font-ds-font-weight-bold leading-ds-space-300 line-clamp-1 text-ellipsis"
                                style={{fontFamily: "Inter", color: token("color.text")}}
                            >
                                {kanbanCardHeading ?? (fillAmaronKanbanDetailSlots === true ? formatKanbanCardFieldValue(null) : null)}
                            </div>
                        </div>
                    </div>
                    <KanbanCardRowActions
                        row={event}
                        config={props.rowActionsConfig}
                        phoneDial={kanbanPhoneDial}
                    />
                </div>
                {amaronEnquiryKanbanSecondaryDetails !== null ? (
                    <div className="px-ds-space-200 pb-ds-space-150">
                        <AmaronKanbanSecondaryDetailsSection
                            serviceRequestedDetail={amaronEnquiryKanbanSecondaryDetails.serviceRequestedDetail}
                            remarksDetail={amaronEnquiryKanbanSecondaryDetails.remarksDetail}
                            utmPlatformDetail={amaronEnquiryKanbanSecondaryDetails.utmPlatformDetail}
                            utmSourceDetail={amaronEnquiryKanbanSecondaryDetails.utmSourceDetail}
                        />
                    </div>
                ) : null}
            </div>
            {localAdditionalButtonModal !== null && props.onRequestAdditionalButtonModal === undefined ? (
                <EventAdditionalButtonFormModal
                    config={localAdditionalButtonModal.config}
                    event={localAdditionalButtonModal.event}
                    eventTemplate={eventTemplate}
                    journeysModuleConfiguration={journeysModuleConfiguration}
                    opened={true}
                    onClose={() => {
                        setLocalAdditionalButtonModal(null);
                    }}
                    queryKeyPrefix={queryKeyPrefixForEmbeddedModal}
                    workspaceUserTemplateId={workspaceUser.templateId}
                    submitDisabledUntilValid={
                        isAmaronWorkspace(workspaceCapabilities) === true &&
                        eventTemplate.id === (AmaronEventTypes.Calls as Uuid) &&
                        localAdditionalButtonModal.config.buttonKind === OttopilotAdditionalButtonKind.cancel
                    }
                />
            ) : null}
        </div>
    );
}
